import { IDriverset } from '../api/models';

// Must have ts-ignore, because there are no types.
// @ts-ignore
import createAquafishModule from './aquafish.mjs';

import { AquaShellModel } from './AquaShellModel';
import { interpolateDriverSet } from './Interpolation';

interface IAquaShellInitParams {
  initialWeight?: number;
  timeStep?: number;
  boxVolume?: number;
  numAnimals?: number;
  triploidStatus?: boolean;
  useBottomCulture?: boolean;
  useDissolvedOxygen?: boolean;
  /** An AquaShellModel value. Typed as number because codes reach us from the
   *  species table, not from the enum. */
  species?: number;
  startTime?: number;
  endTime?: number;    
}

const DefaultAquaShellInitParams: IAquaShellInitParams = {
  initialWeight: 1,
  timeStep: 1,
  boxVolume: 1,
  numAnimals: 1,
  triploidStatus: true,
  useBottomCulture: true,
  useDissolvedOxygen: true,
  species: AquaShellModel.AQUASHELLPACIFICOYSTERAMC,
  startTime: 0,
  endTime: 10      
}

interface IAquaShellDay {
  /** Elapsed culture day, 0-based. Not the model's internal clock, which
   *  starts at AStartTime — see the conversion below. */
  modelTime: number;
  individualWeight: number;
  individualLength: number;
  clearanceRate: number;
  phytoplanktonUptake: number;
  detritusUptake: number;
  pomUptake: number;
  faecesBiodeposition: number;
  pomBiodeposition: number;
  ammoniaExcretion: number;
}

interface IAquaShellResult {
  days: IAquaShellDay[];
}

class AquaShellFacade {

  static call = async (initParams: IAquaShellInitParams, driverset: IDriverset): Promise<IAquaShellResult> => {
    const aquafish = await createAquafishModule();

    const interpolation = interpolateDriverSet(driverset);

    // Create and initialize AquaFishInitParams
    const _initParams = new aquafish.ShellfishInitParams();
    _initParams.AInitialWeight = initParams.initialWeight ?? DefaultAquaShellInitParams.initialWeight;
    _initParams.ATimeStep = initParams.timeStep ?? DefaultAquaShellInitParams.timeStep;   
    _initParams.ABoxVolume = initParams.boxVolume ?? DefaultAquaShellInitParams.boxVolume;
    _initParams.ANumberOfAnimals = initParams.numAnimals ?? DefaultAquaShellInitParams.numAnimals;
    _initParams.ATriploidStatus = initParams.triploidStatus ?? DefaultAquaShellInitParams.triploidStatus;
    _initParams.AUseBottomCulture = initParams.useBottomCulture ?? DefaultAquaShellInitParams.useBottomCulture;
    _initParams.AUseDissolvedOxygen = initParams.useDissolvedOxygen ?? DefaultAquaShellInitParams.useDissolvedOxygen;
    _initParams.ASpeciesCode = initParams.species ?? DefaultAquaShellInitParams.species;
    _initParams.AStartTime = initParams.startTime ?? DefaultAquaShellInitParams.startTime;
    _initParams.AEndTime = initParams.endTime ?? DefaultAquaShellInitParams.endTime;      

    const chlorophyllArray = new aquafish.VectorDouble();
    const POMArray = new aquafish.VectorDouble();
    const TPMArray = new aquafish.VectorDouble();
    const waterTemperatureArray = new aquafish.VectorDouble();
    const salinityArray = new aquafish.VectorDouble();
		const dissolvedOxygenArray = new aquafish.VectorDouble();

    // FinFish.cpp/ShellFish.cpp treat AEndTime as a *duration* (`runTime`), not an
    // absolute end: the model loops modelTime from AStartTime to
    // AStartTime + AEndTime inclusive, stepping by ATimeStep, and consumes one
    // driver value per iteration. The iteration count is therefore independent of
    // AStartTime. Mirror that loop exactly here so the arrays are always the length
    // the model reads; sizing them as (AEndTime - AStartTime + 1) only happened to
    // work while AStartTime was always 0, and would under-supply the model — an
    // unchecked out-of-bounds vector read in WASM — for any later start day.
    //
    // idx walks the interpolated year from the culture start day, wrapping at the
    // 365-day boundary. AStartTime is a 0-based day-of-year (0 = 1 January), which
    // is the same basis interpolateDriverSet uses, so it doubles as the offset.
    // Only rows 0..364 are read; interpolateDriverSet's 366th row duplicates row 0
    // and is not needed here.
    let i = 0;
    for (let modelTime = _initParams.AStartTime;
         modelTime <= _initParams.AStartTime + _initParams.AEndTime;
         modelTime += _initParams.ATimeStep) {
      const idx = (_initParams.AStartTime + i) % 365;
      chlorophyllArray.push_back(interpolation[idx].chlorophyll);
      POMArray.push_back(interpolation[idx].pom);
      TPMArray.push_back(interpolation[idx].tpm);
      waterTemperatureArray.push_back(interpolation[idx].waterTemperature);
      salinityArray.push_back(interpolation[idx].salinity);
      dissolvedOxygenArray.push_back(interpolation[idx].dissolvedOxygen);
      i++;
    }

    const result = aquafish.runAquashellModel(_initParams, 
      chlorophyllArray, POMArray, TPMArray, waterTemperatureArray, salinityArray, dissolvedOxygenArray);

    // Convert results to plain JS array:
    const days: IAquaShellDay[] = [];
    for (let i = 0; i < result.results.size(); i++) {
      const r = result.results.get(i);
      days.push({
        // The model's ModelTime runs from AStartTime, so a culture starting in
        // June reports 151..550 rather than 0..399. Callers plot and index this
        // as elapsed days, so rebase it here and keep AStartTime internal.
        modelTime: r.ModelTime - _initParams.AStartTime,
        individualWeight: r.IndividualWeight,
        individualLength: r.IndividualLength,
        clearanceRate: r.ClearanceRate,
        phytoplanktonUptake: r.PhytoplanktonUptake, 
        detritusUptake: r.DetritusUptake,
        pomUptake: r.POMUptake,
        faecesBiodeposition: r.FaecesBiodeposition,
        pomBiodeposition: r.POMBiodeposition,
        ammoniaExcretion: r.AmmoniaExcretion
      });
    }

    const aquashellresult: IAquaShellResult = {
      days: days
    }

    return aquashellresult;
  }
}

export { AquaShellFacade, IAquaShellResult }
